// src/pages/Reports/components/TicketDeliveryDetailTable.tsx
// Mục 15.a/15.c/15.d, README_UPDATE.md — bảng chi tiết các lần giao vé, dùng chung cho: modal xem
// chi tiết của báo cáo giao vé đại lý (lọc theo agentId), báo cáo kỳ phát hành (lọc theo planId),
// và phần "luồng giao" của modal chi tiết công nợ đại lý (lọc theo cả agentId + planId).

import React, { useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getTicketDeliveryDetailApi } from 'helpers/fakebackend_helper';
import { ReportPagination, TicketDeliveryDetailRow, TicketDeliveryDetailSummary } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_components');

interface TicketDeliveryDetailTableProps {
  planId?: number;
  agentId?: number;
  // Ẩn cột Đại lý khi đang xem chi tiết CỦA đúng 1 đại lý (agentId cố định) — không cần lặp lại.
  showAgentColumn?: boolean;
  getAgentLabel?: (agentId: number) => string;
}

const PAGE_SIZE = 10;

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '0';

const formatCurrency = (value?: number | null) =>
  typeof value === 'number' ? st('value_d_text', { value: value.toLocaleString('vi-VN') }) : '-';

const formatDate = (value?: string | null) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : '-');

// Loại bỏ param rỗng/undefined trước khi gọi API — xem README_RULE.md mục 1.
const cleanParams = (params: any): any => {
  const cleaned: any = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

const TicketDeliveryDetailTable: React.FC<TicketDeliveryDetailTableProps> = ({
  planId,
  agentId,
  showAgentColumn = true,
  getAgentLabel,
}) => {
  const [rows, setRows] = useState<TicketDeliveryDetailRow[]>([]);
  const [summary, setSummary] = useState<TicketDeliveryDetailSummary>({ totalQuantity: 0, totalValue: 0 });
  const [pagination, setPagination] = useState<ReportPagination>({
    currentPage: 1,
    pageSize: PAGE_SIZE,
    totalItems: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getTicketDeliveryDetailApi(
      cleanParams({
        PlanId: planId,
        AgentId: agentId,
        PageIndex: pagination.currentPage,
        PageSize: pagination.pageSize,
      }),
    )
      .then((res: any) => {
        if (cancelled) return;
        const payload = res?.data || res;
        setRows(payload?.page?.items || []);
        setSummary(payload?.summary || { totalQuantity: 0, totalValue: 0 });
        setPagination((prev) => ({
          ...prev,
          totalItems: payload?.page?.totalItems || 0,
          totalPages: payload?.page?.totalPages || 0,
        }));
      })
      .catch((error: any) => {
        if (!cancelled) toast.error(error?.message || st('khong_the_tai_chi_tiet_giao_message'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, agentId, pagination.currentPage, pagination.pageSize]);

  const columnCount = showAgentColumn ? 7 : 6;
  const leadingColumnCount = showAgentColumn ? 4 : 3;

  return (
    <>
      {loading ? (
        <div className="text-center py-4">
          <Spinner color="primary" size="sm" />
        </div>
      ) : (
        <DataTableShell
          headerRow={
            <tr>
              <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
              <th>{st('ma_phieu_giao_column')}</th>
              <th>{st('ky_phat_hanh_column')}</th>
              {showAgentColumn && <th>{st('dai_ly_column')}</th>}
              <th className="text-end">{st('so_luong_ve_column')}</th>
              <th className="text-end">{st('gia_tri_column')}</th>
              <th>{st('thoi_gian_giao_column')}</th>
            </tr>
          }
          footerRow={
            <tr>
              <td colSpan={leadingColumnCount} className="text-end fw-semibold">
                {st('tong_cong_text')}
              </td>
              <td className="text-end fw-semibold">{formatNumber(summary.totalQuantity)}</td>
              <td className="text-end fw-semibold">{formatCurrency(summary.totalValue)}</td>
              <td></td>
            </tr>
          }
        >
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="text-center text-muted py-4">
                {st('khong_co_du_lieu_text')}
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={row.deliveryId}>
                <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                <td className="fw-medium text-primary">{row.deliveryCode}</td>
                <td>{row.planCode || row.issuancePlanId}</td>
                {showAgentColumn && <td>{getAgentLabel ? getAgentLabel(row.agentId) : row.agentId}</td>}
                <td className="text-end">{formatNumber(row.quantity)}</td>
                <td className="text-end">{formatCurrency(row.value)}</td>
                <td>{formatDate(row.deliveredAt)}</td>
              </tr>
            ))
          )}
        </DataTableShell>
      )}

      <AgentPagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalItems={pagination.totalItems}
        pageSize={pagination.pageSize}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
      />
    </>
  );
};

export default TicketDeliveryDetailTable;
